import { Link } from "react-router-dom";
import Reveal from "@/components/feature/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import ProductCard from "@/components/feature/ProductCard";
import ProjectCard from "@/components/feature/ProjectCard";
import { products } from "@/mocks/products";
import { projects } from "@/mocks/projects";

const categories = [
  {
    name: "천연대리석",
    en: "Natural Marble",
    desc: "이탈리아·스페인산 프리미엄 대리석으로 공간의 품격을 완성합니다.",
    image:
      "https://readdy.ai/api/search-image?query=white%20marble%20slabs%20displayed%20upright%20in%20a%20bright%20minimal%20stone%20showroom%2C%20soft%20natural%20light%2C%20neutral%20beige%20walls%2C%20elegant%20architectural%20interior%20photography%2C%20high%20detail&width=800&height=1000&seq=stone-cat-01&orientation=portrait",
    icon: "ri-vip-diamond-line",
    to: "/products?category=%EC%B2%9C%EC%97%B0%EB%8C%80%EB%A6%AC%EC%84%9D",
  },
  {
    name: "인조석",
    en: "Engineered Stone",
    desc: "흡수율이 낮아 관리가 쉬운 엔지니어드 쿼츠 상판을 제안합니다.",
    image:
      "https://readdy.ai/api/search-image?query=modern%20kitchen%20interior%20with%20white%20engineered%20quartz%20countertop%20and%20warm%20wood%20cabinet%2C%20soft%20daylight%2C%20minimal%20neutral%20palette%2C%20editorial%20interior%20photography%2C%20clean%20composition&width=800&height=1000&seq=stone-cat-02&orientation=portrait",
    icon: "ri-instance-line",
    to: "/products?category=%EC%9D%B8%EC%A1%B0%EC%84%9D",
  },
  {
    name: "트래버틴",
    en: "Travertine",
    desc: "자연의 층이 그대로 살아 있는 따뜻한 베이지 톤 석재입니다.",
    image:
      "https://readdy.ai/api/search-image?query=serene%20bathroom%20interior%20finished%20with%20warm%20beige%20travertine%20walls%20and%20floor%2C%20stone%20bathtub%2C%20soft%20indirect%20lighting%2C%20natural%20textures%2C%20spa%20like%20atmosphere%2C%20interior%20photography&width=800&height=1000&seq=stone-cat-03&orientation=portrait",
    icon: "ri-water-flash-line",
    to: "/products?category=%ED%8A%B8%EB%9E%98%EB%B2%84%ED%8B%B4",
  },
  {
    name: "테라조",
    en: "Terrazzo",
    desc: "다양한 컬러칩이 리드미컬하게 박힌 현대적인 표면 소재입니다.",
    image:
      "https://readdy.ai/api/search-image?query=contemporary%20commercial%20space%20with%20polished%20terrazzo%20floor%20and%20stone%20reception%20counter%2C%20bright%20minimal%20interior%2C%20soft%20natural%20light%2C%20architectural%20photography%2C%20clean%20lines&width=800&height=1000&seq=stone-cat-04&orientation=portrait",
    icon: "ri-blur-off-line",
    to: "/products?category=%ED%85%8C%EB%9D%BC%EC%A1%B0",
  },
  {
    name: "화강석",
    en: "Granite",
    desc: "강도가 높아 실내외 구조 요소에 두루 쓰이는 단단한 석재입니다.",
    image:
      "https://readdy.ai/api/search-image?query=modern%20building%20exterior%20facade%20clad%20with%20grey%20granite%20stone%20panels%2C%20clean%20minimal%20architecture%2C%20soft%20daylight%2C%20neutral%20tones%2C%20architectural%20photography%2C%20crisp%20detail&width=800&height=1000&seq=stone-cat-05&orientation=portrait",
    icon: "ri-building-2-line",
    to: "/products?category=%ED%99%94%EA%B0%95%EC%84%9D",
  },
  {
    name: "세라믹",
    en: "Porcelain Ceramic",
    desc: "대리석 무늬를 얇고 가볍게, 대형 포세린 타일로 구현합니다.",
    image:
      "https://readdy.ai/api/search-image?query=luxury%20walk%20in%20shower%20interior%20with%20large%20format%20grey%20marble%20look%20porcelain%20tiles%2C%20soft%20lighting%2C%20minimal%20neutral%20bathroom%20design%2C%20high%20end%20interior%20photography&width=800&height=1000&seq=stone-cat-06&orientation=portrait",
    icon: "ri-layout-grid-line",
    to: "/products?category=%EC%84%B8%EB%9D%BC%EB%AF%B9",
  },
];

const stats = [
  { value: "32년", label: "석재 시공 경력", icon: "ri-history-line" },
  { value: "4,800+", label: "누적 시공 프로젝트", icon: "ri-building-line" },
  { value: "1,200+", label: "보유 소재 컬렉션", icon: "ri-stack-line" },
  { value: "2곳", label: "직영 전시장", icon: "ri-store-2-line" },
];

const strengths = [
  {
    icon: "ri-scan-2-line",
    title: "직접 보고 고르는 소재",
    desc: "서울 본사와 부산 전시장에서 1,200여 종의 실물 소재를 직접 비교하실 수 있습니다.",
  },
  {
    icon: "ri-tools-line",
    title: "설계부터 시공까지",
    desc: "현장 실측, 도면 검토, 시공까지 한 팀이 책임지고 진행해 오차를 줄입니다.",
  },
  {
    icon: "ri-shield-check-line",
    title: "10년 품질 보증",
    desc: "시공 완료 후에도 관리 방법 안내와 사후 점검을 통해 오래 쓰는 공간을 만듭니다.",
  },
];

export default function Home() {
  const featured = products.slice(0, 4);
  const showcase = projects.slice(0, 4);

  return (
    <>
      {/* Hero */}
      <section className="relative flex min-h-[560px] w-full items-center justify-center overflow-hidden md:min-h-[680px]">
        <img
          src="https://readdy.ai/api/search-image?query=elegant%20luxury%20open%20plan%20kitchen%20and%20living%20interior%20with%20white%20marble%20island%20and%20stone%20wall%20finishes%2C%20warm%20natural%20daylight%20from%20tall%20windows%2C%20neutral%20warm%20palette%2C%20high%20end%20residential%20architectural%20photography%2C%20wide%20angle%2C%20refined%20composition&width=1800&height=1100&seq=stone-home-hero&orientation=landscape"
          alt="대리석 상판과 석재 마감으로 완성된 고급 주거 공간 인테리어"
          title="동우스톤 천연석 인테리어 시공 사례"
          className="hero-zoom absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/55 via-foreground-950/40 to-foreground-950/60" />
        <div className="relative z-10 mx-auto w-full max-w-content px-4 pt-28 text-center md:px-6">
          <p className="font-label text-xs font-semibold tracking-[0.34em] text-primary-200">
            NATURAL STONE · ENGINEERED STONE
          </p>
          <h1 className="mx-auto mt-6 max-w-3xl font-heading text-4xl leading-[1.15] text-background-50 md:text-6xl">
            공간에 오래 남는
            <br />
            단단한 아름다움
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-sm leading-relaxed text-background-100/85 md:text-base">
            천연대리석부터 엔지니어드 스톤, 포세린까지. 동우스톤은 32년의 시공 경험으로 소재 선정부터 마감까지
            함께합니다. 실사로 담긴 공간을 보고, 나에게 맞는 석재를 찾아보세요.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/products"
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
            >
              <i className="ri-layout-masonry-line" /> 제품 살펴보기
            </Link>
            <Link
              to="/projects"
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-100/50 px-7 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 sm:w-auto"
            >
              <i className="ri-image-2-line" /> 시공 사례 보기
            </Link>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-background-100/70 md:flex">
          <span className="font-label text-[10px] tracking-[0.3em]">SCROLL</span>
          <i className="ri-arrow-down-line animate-bounce text-lg" />
        </div>
      </section>

      {/* Showroom highlight */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-store-3-line text-xl" />
              </span>
              <div>
                <h3 className="font-heading text-base text-foreground-950">부산 전시장 그랜드 오픈</h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                  해운대에서 만나는 동우스톤의 새로운 공간. 실물 소재를 직접 비교해 보세요.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                <i className="ri-book-open-line text-xl" />
              </span>
              <div>
                <h3 className="font-heading text-base text-foreground-950">2026 카탈로그</h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                  신제품 엔지니어드 스톤 컬렉션을 포함한 전체 소재를 담았습니다.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                <i className="ri-customer-service-2-line text-xl" />
              </span>
              <div>
                <h3 className="font-heading text-base text-foreground-950">무료 견적 상담</h3>
                <p className="mt-1 text-xs leading-relaxed text-foreground-600">
                  도면과 현장 사진을 보내주시면 담당자가 맞춤 견적을 안내해 드립니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand intro */}
      <section className="bg-background-50">
        <div className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-24">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <div className="relative h-[380px] w-full overflow-hidden rounded-lg md:h-[520px]">
                <img
                  src="https://readdy.ai/api/search-image?query=craftsman%20hand%20gently%20touching%20a%20polished%20marble%20stone%20slab%20surface%20in%20a%20bright%20showroom%2C%20warm%20natural%20light%2C%20minimal%20neutral%20background%2C%20artisanal%20material%20photography%2C%20shallow%20depth%20of%20field%2C%20refined%20detail&width=1100&height=1300&seq=stone-home-intro&orientation=portrait"
                  alt="동우스톤 전시장에서 대리석 표면을 확인하는 모습"
                  title="동우스톤 프리미엄 석재 큐레이션"
                  className="h-full w-full object-cover object-top"
                  loading="lazy"
                />
              </div>
            </Reveal>
            <Reveal delay={120}>
              <SectionHeading
                eyebrow="ABOUT DONGWOO"
                title={
                  <>
                    좋은 석재는
                    <br />
                    좋은 공간에서 완성됩니다
                  </>
                }
                description="동우스톤은 1994년부터 석재를 다뤄온 종합 석재 브랜드입니다. 광산에서 직접 선별한 원석과 정밀 가공 설비를 통해 결이 고른 판재만을 공급하며, 설계자와 시공자가 신뢰할 수 있는 소재를 제안합니다."
              />
              <ul className="mt-8 space-y-5">
                {strengths.map((item) => (
                  <li key={item.title} className="flex gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                    <div>
                      <h3 className="font-heading text-base text-foreground-950">{item.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <Link
                to="/brand"
                className="mt-9 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-foreground-300 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                브랜드 스토리 <i className="ri-arrow-right-line" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-24">
          <Reveal>
            <SectionHeading
              align="center"
              eyebrow="CATEGORY"
              title="공간에 맞는 석재를 고르세요"
              description="천연석의 깊이감, 엔지니어드 스톤의 실용성, 포세린의 경량성까지. 카테고리별로 나에게 맞는 소재를 찾아볼 수 있습니다."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map((category, index) => (
              <Reveal key={category.name} delay={index * 80}>
                <Link
                  to={category.to}
                  className="group relative block h-[380px] cursor-pointer overflow-hidden rounded-lg md:h-[440px]"
                >
                  <img
                    src={category.image}
                    alt={`${category.name} 소재 인테리어`}
                    title={`${category.name} 동우스톤`}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-50/95 text-primary-600">
                      <i className={`${category.icon} text-xl`} />
                    </span>
                    <p className="mt-4 font-label text-[11px] tracking-[0.22em] text-primary-200">{category.en}</p>
                    <h3 className="mt-1 font-heading text-2xl text-background-50">{category.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-background-100/80">{category.desc}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-background-50">
                      자세히 보기 <i className="ri-arrow-right-line" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className="bg-background-50">
        <div className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-24">
          <Reveal>
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <SectionHeading
                eyebrow="BEST COLLECTION"
                title="가장 많이 찾는 소재"
                description="주방 상판과 욕실, 거실 바닥 등 활용도가 높은 인기 제품을 모았습니다."
              />
              <Link
                to="/products"
                className="inline-flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                전체 제품 보기 <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </Reveal>
          <div data-product-shop className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {featured.map((product, index) => (
              <Reveal key={product.id} delay={index * 80}>
                <ProductCard product={product} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-accent-900">
        <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-16">
          <div className="grid grid-cols-2 gap-8 lg:grid-cols-4">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-background-100/10 text-primary-200">
                  <i className={`${stat.icon} text-2xl`} />
                </span>
                <p className="mt-4 font-heading text-3xl text-background-50 md:text-4xl">{stat.value}</p>
                <p className="mt-1.5 text-xs text-background-200/75 md:text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project showcase */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-content px-4 py-16 md:px-6 md:py-24">
          <Reveal>
            <SectionHeading
              eyebrow="PROJECT"
              title="실제 공간에 적용된 석재"
              description="주거부터 호텔, 상업 공간까지. 동우스톤의 소재가 완성한 공간을 실사 이미지로 확인하세요."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <Reveal className="lg:row-span-2">
              <ProjectCard project={showcase[0]} large />
            </Reveal>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              {showcase.slice(1).map((project, index) => (
                <Reveal key={project.id} delay={index * 80}>
                  <ProjectCard project={project} />
                </Reveal>
              ))}
            </div>
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/projects"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              시공 사례 전체 보기 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA + newsletter */}
      <section className="relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=warm%20sunlit%20bathroom%20interior%20finished%20with%20beige%20stone%20walls%20and%20marble%20vanity%20top%2C%20soft%20morning%20light%20through%20sheer%20curtains%2C%20calm%20neutral%20palette%2C%20luxury%20interior%20photography%2C%20serene%20atmosphere&width=1700&height=900&seq=stone-home-cta&orientation=landscape"
          alt="베이지 석재로 마감한 욕실 인테리어"
          title="동우스톤 상담 문의"
          className="absolute inset-0 h-full w-full object-cover object-top"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-foreground-950/72" />
        <div className="relative z-10 mx-auto max-w-content px-4 py-16 md:px-6 md:py-24">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="font-label text-xs font-semibold tracking-[0.3em] text-primary-200">CONTACT US</p>
              <h2 className="mt-4 font-heading text-3xl leading-tight text-background-50 md:text-4xl">
                어떤 석재가 좋을지
                <br />
                고민되시나요?
              </h2>
              <p className="mt-5 max-w-lg text-sm leading-relaxed text-background-100/85 md:text-base">
                도면이나 현장 사진을 보내주시면 용도와 예산에 맞는 소재를 추천해 드립니다. 카탈로그 요청과
                견적 상담 모두 고객문의 페이지에서 접수하실 수 있습니다.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/contact"
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
                >
                  <i className="ri-customer-service-2-line" /> 상담 문의하기
                </Link>
                <a
                  href="tel:0517412045"
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 px-7 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 sm:w-auto"
                >
                  <i className="ri-phone-line" /> 051-741-2045
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Link
                to="/contact"
                className="group flex flex-col justify-between rounded-lg bg-background-50/95 p-6 backdrop-blur-sm transition-colors hover:bg-background-50"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-book-open-line text-xl" />
                </span>
                <div className="mt-6">
                  <h3 className="font-heading text-xl text-foreground-950">Catalogue</h3>
                  <p className="mt-1 text-sm text-foreground-600">2026 카탈로그 요청하기</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-primary-600">
                    신청하기 <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </Link>
              <Link
                to="/contact"
                className="group flex flex-col justify-between rounded-lg bg-background-50/95 p-6 backdrop-blur-sm transition-colors hover:bg-background-50"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                  <i className="ri-question-answer-line text-xl" />
                </span>
                <div className="mt-6">
                  <h3 className="font-heading text-xl text-foreground-950">Inquiry</h3>
                  <p className="mt-1 text-sm text-foreground-600">견적·시공 상담 문의하기</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-accent-700">
                    문의하기 <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}